import {
  DatePicker as DatePickerMui,
  DatePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import React, { useEffect, useState, useRef } from "react";
import { getDateFormat } from "utils";
import { InputAdornment, SxProps, Theme } from '@mui/material';
import { COLORS } from "components/consts";
import { EsgCalender } from "assets";
import { TextField, TextFieldProps } from "./text-field";

export const EsgDatePickerField = ({
  placeholdervalue,
  disabled,
  clearDate,
  emission,
  filterAll,
  width,
  setDate,
  format = getDateFormat(),
  textFieldProps = {},
  onKeyDown,
  position,
  sx,
  inputSx,
  required,
  placeholderSx = {},
  ...datePickerProps
}: {
  disabled?: boolean;
  textFieldProps?: TextFieldProps;
  clearDate?: boolean;
  placeholdervalue?: string;
  emission?: boolean;
  filterAll?: boolean;
  setDate?: any;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  width?: string;
  position?: "start" | "end";
  sx?: SxProps<Theme>;
  inputSx?: SxProps<Theme>;
  required?: boolean;
  placeholderSx?: React.CSSProperties;
} & Omit<
  DatePickerProps<Dayjs>,
  "renderInput" | "date" | "openPicker" | "rawValue"
>) => {

  const [value, setValue] = useState<any>(datePickerProps.value);
  const prevValueRef = useRef<any>(datePickerProps.value);

  useEffect(() => {
    if (clearDate === true) {
      setValue(null);
      prevValueRef.current = null;
    }
  }, [clearDate]);

  useEffect(() => {
    // Only update if the date value actually changed (compare by formatted string to avoid reference issues)
    const prevValueStr = prevValueRef.current ? dayjs(prevValueRef.current).format("YYYY-MM-DD") : null;
    const newValueStr = datePickerProps.value ? dayjs(datePickerProps.value).format("YYYY-MM-DD") : null;

    if (prevValueStr !== newValueStr) {
      setValue(datePickerProps.value);
      prevValueRef.current = datePickerProps.value;
    }
  }, [datePickerProps.value]);

  const handleClear = () => {
    setValue(null);
  }

  useEffect(() => {
    if (disabled) {
      handleClear();
    }
  }, [disabled]);

  const calenderIcon = () => <InputAdornment position="end">
    <EsgCalender />
  </InputAdornment>

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DatePickerMui
        disabled={disabled}
        format={format}
        {...datePickerProps}
        value={value}
        slotProps={{
          inputAdornment: {
            position: position || 'start',
          },
          textField: {
            placeholder: placeholdervalue || "Month",
            fullWidth: true,
            required,
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
              e.stopPropagation();
              if (e.key === "Enter" && setDate && value) {
                setDate(dayjs(value).format("DD/MM/YYYY"));
              }
              onKeyDown?.(e);
            },
            InputProps: {
              sx: {
                "& .MuiInputBase-input::placeholder": {
                  ...placeholderSx,
                },
              },
              style: {
                borderRadius: "8px",
                height: "36px",
                color: COLORS.Romance,
                fontWeight: 500,
                width,
              },
            },
            sx: {
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.Romance,
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.Romance,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: COLORS.Romance,
              },
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "& fieldset": {
                  border: "1px solid",
                  borderColor: COLORS.Romance,
                  fontFamily: "Inter",
                },
                "&:hover fieldset": {
                  borderColor: COLORS.Romance,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.Romance,
                },
              },
              color: COLORS.Romance,
              width,
              ...sx,
            },
            InputLabelProps: {
              shrink: false,
            },
            ...textFieldProps,
          },
        }}
        slots={{
          openPickerIcon: calenderIcon,
          textField: TextField as any
        }}
      />
    </LocalizationProvider>
  );
};
